export const SIGN_IN_FAILURE_COPY = {
  invalid_credentials: {
    title: 'Check your email and password',
    description: 'The email or password is incorrect. Try again, or use Forgot password.',
  },
  email_unconfirmed: {
    title: 'Confirm your email',
    description: 'Confirm your email before signing in. Check your inbox for the confirmation link.',
  },
  account_deactivated: {
    title: 'Account deactivated',
    description: 'This account is deactivated. Contact your company administrator to restore access.',
  },
  password_expired: {
    title: 'Password expired',
    description: 'Your password has expired. Use Forgot password to set a new one, then sign in.',
  },
  mfa_enrollment_required: {
    title: 'Extra verification required',
    description:
      'Your company requires multi-factor authentication before you can sign in. Ask an administrator to enroll you.',
  },
  account_incomplete: {
    title: 'Account needs attention',
    description: 'This account is missing setup details, so sign-in cannot finish. Contact support.',
  },
  auth_unavailable: {
    title: 'Sign-in is temporarily unavailable',
    description: 'We could not sign you in just now. Wait a moment and try again.',
  },
  mfa_invalid: {
    title: 'Check the verification code',
    description: 'That verification code is incorrect or expired. Enter the latest code and try again.',
  },
} as const;

export type SignInFailureCode = keyof typeof SIGN_IN_FAILURE_COPY;

export interface SignInFailure {
  title: string;
  description: string;
  code: SignInFailureCode;
}

const GENERIC_MESSAGES = new Set([
  'authentication failed',
  'unauthorized',
  'forbidden',
  'bad request',
  'internal server error',
  'http exception',
  'invalid user data',
  'failed to validate user',
  'mfa authentication failed',
  'invalid credentials',
  'request failed',
]);

function asText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === 'string').join(' ').trim();
  }
  return '';
}

function knownCode(value: unknown): SignInFailureCode | undefined {
  if (typeof value === 'string' && value in SIGN_IN_FAILURE_COPY) {
    return value as SignInFailureCode;
  }
  return undefined;
}

function inferCode(
  message: string,
  status: number | undefined,
  flags: { requiresPasswordChange?: boolean; requiresMFAEnrollment?: boolean },
): SignInFailureCode {
  if (flags.requiresPasswordChange) return 'password_expired';
  if (flags.requiresMFAEnrollment) return 'mfa_enrollment_required';

  const text = message.toLowerCase();
  if (text.includes('confirm your email') || text.includes('email address before')) return 'email_unconfirmed';
  if (text.includes('deactivat')) return 'account_deactivated';
  if (text.includes('password has expired') || text.includes('forgot password to set a new one')) return 'password_expired';
  if (text.includes('mfa enrollment') || text.includes('multi-factor')) return 'mfa_enrollment_required';
  if (text.includes('verification code') || text.includes('invalid mfa')) return 'mfa_invalid';
  if (text.includes('missing setup') || text.includes('invalid user data')) return 'account_incomplete';
  if (
    (status !== undefined && status >= 500) ||
    text.includes('econn') ||
    text.includes('timeout') ||
    text.includes('timed out') ||
    text.includes('temporarily unavailable') ||
    text.includes('could not sign you in')
  ) {
    return 'auth_unavailable';
  }
  if (text.includes('email or password')) return 'invalid_credentials';
  if (status === 403) return 'account_incomplete';
  return 'invalid_credentials';
}

export function describeSignInFailure(input: {
  status?: number;
  message?: unknown;
  code?: unknown;
  requiresPasswordChange?: boolean;
  requiresMFAEnrollment?: boolean;
}): SignInFailure {
  const message = asText(input.message);
  const code =
    knownCode(input.code) ||
    inferCode(message, input.status, {
      requiresPasswordChange: input.requiresPasswordChange,
      requiresMFAEnrollment: input.requiresMFAEnrollment,
    });
  const copy = SIGN_IN_FAILURE_COPY[code];
  const generic = !message || GENERIC_MESSAGES.has(message.toLowerCase()) || message.length > 240;
  return {
    code,
    title: copy.title,
    description: generic ? copy.description : message,
  };
}
